/**
 * Safe URL handling for Link-in-Bio and other user-generated URLs.
 *
 * normalizeExternalUrl() mirrors the backend `_normalize_external_url` —
 * bare domains get an `https://` prefix so they don't resolve as relative
 * paths (root cause of the "tadlo.ai/instagram.com" bug). Protocol URLs
 * (mailto:, tel:, sms:, ftp:, https:) are passed through unchanged.
 */

const SCHEME_RE = /^[a-z][a-z0-9+.\-]*:/i;
const BARE_HOST_RE = /^[a-z0-9][a-z0-9\-._]*\.[a-z]{2,}/i;

export function normalizeExternalUrl(raw) {
  if (raw == null) return "";
  if (typeof raw !== "string") return "";
  const s = raw.trim();
  if (!s) return "";
  if (s.startsWith("//")) return "https:" + s;
  if (SCHEME_RE.test(s)) return s;
  if (BARE_HOST_RE.test(s)) return "https://" + s;
  return s;
}

/**
 * Open a user-provided URL in a new tab. Always normalizes first so bare
 * domains don't resolve as relative paths. Silently no-ops on empty input.
 */
export function openExternalUrl(rawUrl) {
  const url = normalizeExternalUrl(rawUrl);
  if (!url) return;
  try {
    // `noopener,noreferrer` blocks tab-napping + referrer leakage.
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    // Fallback: same-tab (very rare — popup blocker / hostile environment).
    window.location.href = url;
  }
}

/* ═══════════════════════════════════════════════════════════════════
 *  PASTED SHARE TEXT -> ONE URL
 * ═══════════════════════════════════════════════════════════════════
 * "Share" on a shop app rarely copies a URL. It copies a MESSAGE that
 * contains one, and usually the same one twice — Myntra's is the exact
 * case reported (P, 2026-08-22):
 *
 *     CAVA Wine Swirl Tennis Skorts
 *     https://www.myntra.com/.../35100678/buy?utm_source=social_share&...
 *     https://www.myntra.com/.../35100678/buy?utm_source=social_share&...
 *
 * Pasted whole into a link field that becomes a DM button, the title and
 * the duplicate go with it and the button does not work. So the paste is
 * reduced to the first usable URL.
 *
 * WHAT IS NOT DONE, DELIBERATELY: the query string is left alone. Those
 * `utm_*` parameters look like noise, and on Myntra they are — but a
 * share link's parameters are the shop's business, and some stores route
 * or authenticate on them. Dropping a parameter that turns out to matter
 * breaks the destination silently, days later, for a creator who has no
 * way to connect it to a paste. Extracting and de-duplicating is the part
 * that is safe to do for someone.
 */

//: Matched greedily to the end of the token, then trimmed below. A URL is
//: bounded by whitespace in pasted text; trailing sentence punctuation is
//: the one thing that regularly rides along with it.
const URL_IN_TEXT_RE = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

//: Punctuation that is far more often the end of a sentence than part of
//: a URL. A closing bracket is only stripped when it is unbalanced, since
//: real URLs (Wikipedia, for one) do contain them.
const TRAILING_JUNK_RE = /[.,;:!?'"]+$/;

function trimUrlTail(url) {
  let out = url.replace(TRAILING_JUNK_RE, "");
  while (out.endsWith(")") && (out.match(/\(/g) || []).length < (out.match(/\)/g) || []).length) {
    out = out.slice(0, -1);
  }
  return out;
}

/** A comparison key: the same link shared twice is the same link, even
 *  if one copy carries a trailing slash or a different scheme case. */
function urlIdentity(url) {
  return normalizeExternalUrl(url).toLowerCase().replace(/\/+$/, "");
}

/**
 * Every distinct URL in `text`, in order, normalized.
 *
 * `extractFirstUrl` is what the single-link fields call; this is the
 * primitive underneath it, exported because de-duplication is the part
 * worth testing directly — "the same link twice is one link" is the
 * whole behaviour, and it is invisible in the first-URL result.
 */
export function extractUrls(text) {
  if (typeof text !== "string" || !text.trim()) return [];
  const seen = new Set();
  const out = [];
  for (const raw of text.match(URL_IN_TEXT_RE) || []) {
    const url = normalizeExternalUrl(trimUrlTail(raw));
    if (!url) continue;
    const key = urlIdentity(url);
    if (seen.has(key)) continue;      // the duplicate line in a share blob
    seen.add(key);
    out.push(url);
  }
  return out;
}

/**
 * The one URL to put in a single-link field.
 *
 * Returns "" when there is nothing URL-shaped, so a caller can leave the
 * field alone rather than blanking what someone typed. A bare domain with
 * no surrounding text ("myntra.com/x") is returned normalized, which is
 * what `normalizeExternalUrl` already did for the typed case.
 */
export function extractFirstUrl(text) {
  const found = extractUrls(text);
  if (found.length) return found[0];
  const single = normalizeExternalUrl(text);
  // Only when the WHOLE input was one bare token — otherwise this would
  // return the share message unchanged and defeat the point.
  return /^\S+$/.test((text || "").trim()) ? single : "";
}

/**
 * `onPaste` handler for a URL field.
 *
 * Cleans on the way IN rather than on blur alone, so the creator sees
 * what was actually stored instead of watching a wall of text sit in the
 * field until focus moves. Falls through to the browser's own paste when
 * the clipboard holds nothing better — never swallow a paste it cannot
 * improve.
 */
export function handleUrlPaste(event, setValue) {
  const text = event?.clipboardData?.getData?.("text");
  if (!text) return;
  const url = extractFirstUrl(text);
  if (!url || url === text.trim()) return;
  event.preventDefault();
  setValue(url);
}
